import { useMount } from 'react-use';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
// example values provided for Macbook M2
interface SystemInfo {
  webgpu: {
    // ex 'metal-3', 'vulkan-1'
    architecture: string;
    // ex 'apple', 'nvidia', 'intel'
    vendor: string;
    supported: boolean;
  };
  webgl: {
    // ex. ANGLE (Apple, ANGLE Metal Renderer: Apple M1 Max, Unspecified Version)
    renderer: string;
    // ex. Google Inc. (Apple)
    vendor: string;
    // ex. WebGL 1.0 (OpenGL ES 2.0 Chromium)
    version: string;
    experimental: boolean;
  };
  system: {
    // ex. 10
    cpuCores: string | number;
  };
}

interface GPUAdapterInfo {
  vendor?: string;
  architecture?: string;
}

interface GPUAdapter {
  requestAdapter(): Promise<GPUAdapter | null>;
  info?: GPUAdapterInfo;
}

interface NavigatorGPU extends Navigator {
  gpu?: {
    requestAdapter(): Promise<GPUAdapter | null>;
  };
}

const getCpuInfo = () => {
  const cpuCores = navigator.hardwareConcurrency ?? 'Unavailable';
  return { cpuCores };
};

const getWebGPUInfo = async () => {
  let supported = false;
  let vendor = 'Unavailable';
  let architecture = 'Unavailable';

  const castedGPUNavigator = navigator as NavigatorGPU;

  if ('gpu' in castedGPUNavigator) {
    try {
      const adapter = await castedGPUNavigator.gpu?.requestAdapter();
      if (adapter) {
        const info = adapter.info ?? null;
        supported = true;
        vendor = info?.vendor ?? 'Unavailable';
        architecture = info?.architecture ?? 'Unavailable';
      }
    } catch (err) {
      console.error('Error requesting GPU adapter:', err);
    }
  }

  return {
    architecture,
    vendor,
    supported,
  };
};

const getWebGLInfo = () => {
  let renderer = 'Unavailable';
  let vendor = 'Unavailable';
  let version = 'Unavailable';
  let experimental = false;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const experimentalgl = canvas.getContext('experimental-webgl');

    if (gl == null && experimentalgl != null) {
      experimental = true;
    }

    const useableContext = gl ?? experimentalgl;

    if (useableContext && useableContext instanceof WebGLRenderingContext) {
      const debugInfo = useableContext.getExtension('WEBGL_debug_renderer_info');
      version = useableContext.getParameter(useableContext.VERSION);
      renderer = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'Unavailable';
      vendor = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'Unavailable';
      // clean up canvas
      useableContext?.getExtension('WEBGL_lose_context')?.loseContext();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    }
  } catch (err) {
    console.error('Error collecting WebGL info:', err);
  }

  return {
    renderer,
    vendor,
    version,
    experimental,
  };
};

export async function collectSystemInfo(): Promise<SystemInfo> {
  const result: SystemInfo = {
    system: getCpuInfo(),
    webgpu: await getWebGPUInfo(),
    webgl: getWebGLInfo(),
  };

  return result;
}

const logSystemInfo = async () => {
  const systemInfo = await collectSystemInfo();
  analytics.addContext(globalContext.viewer.systemInfo, systemInfo);
};

export const useSystemInfoAnalytics = () => {
  useMount(() => {
    logSystemInfo();
  });
};
